import { useCallback, useEffect, useRef, useState } from "react";
import { useBrowseModeSwipe } from "./useBrowseModeSwipe";

type Options = {
  enabled: boolean;
  onCommitForYou: () => void;
};

/**
 * Gallery-style Alla → För dig swipe.
 * Both panes slide 1:1 with the finger (like image carousels).
 */
export function useAllaToForYouSwipe({ enabled, onCommitForYou }: Options) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0); // 0..1 toward För dig
  const [settling, setSettling] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const settlingRef = useRef(false);
  const commitRef = useRef(onCommitForYou);
  commitRef.current = onCommitForYou;

  const finishCommit = useCallback(() => {
    if (settlingRef.current) return;
    settlingRef.current = true;
    setReleasing(false);
    setSettling(true);
    setProgress(1);
    window.setTimeout(() => {
      commitRef.current();
      window.setTimeout(() => {
        settlingRef.current = false;
        setSettling(false);
        setProgress(0);
      }, 60);
    }, 280);
  }, []);

  useBrowseModeSwipe(targetRef, {
    enabled: enabled && !settling,
    thresholdPx: 48,
    onProgress: (p) => {
      setReleasing(false);
      setProgress(Math.max(0, p));
    },
    onCancel: () => {
      if (settlingRef.current) return;
      setReleasing(true);
      setProgress(0);
      window.setTimeout(() => setReleasing(false), 280);
    },
    onSwipeLeft: finishCommit,
  });

  const animateToForYou = useCallback(() => {
    // Start visible peek so För dig content mounts, then settle
    setProgress((prev) => (prev < 0.08 ? 0.08 : prev));
    window.requestAnimationFrame(() => finishCommit());
  }, [finishCommit]);

  useEffect(() => {
    if (!enabled) {
      setProgress(0);
      setSettling(false);
      setReleasing(false);
      settlingRef.current = false;
    }
  }, [enabled]);

  const p = progress;
  const animated = settling || releasing;
  const ease = "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)";

  // Full-width gallery slide (no blur / scale — content stays readable)
  const allStyle: React.CSSProperties =
    p > 0.001 || releasing
      ? {
          transform: `translate3d(${-p * 100}%, 0, 0)`,
          transition: animated ? ease : "none",
          willChange: "transform",
          pointerEvents: settling ? "none" : undefined,
        }
      : undefined;

  const incomingStyle: React.CSSProperties = {
    transform: `translate3d(${(1 - p) * 100}%, 0, 0)`,
    transition: animated ? ease : "none",
    willChange: "transform",
  };

  return {
    targetRef,
    progress: p,
    settling,
    releasing,
    allStyle,
    incomingStyle,
    showIncoming: p > 0.01 || settling,
    animateToForYou,
  };
}
